import { inject, Injectable } from '@angular/core';
import { ReplaySubject, Observable } from 'rxjs';
import { WINDOW_TOKEN } from '../../../services/window.token';

import {
  JoyrideStepDoesNotExist,
  JoyrideStepOutOfRange,
  JoyrideStepInfo,
  JoyrideScroll,
  JoyrideStep,
  StepActionType
} from '../joyride.domain';
import { JoyrideConstants } from '../joyride.constants';

import { JoyrideBackdropService } from './joyride-backdrop.service';
import { JoyrideEventListenerService } from './joyride-event-listener.service';
import { JoyrideStepsContainerService } from './joyride-steps-container.service';
import { JoyrideDocumentService } from './joyride-document.service';
import { JoyrideStepDrawerService } from './joyride-step-drawer.service';
import { JoyrideOptionsService } from './joyride-options.service';

export interface IJoyrideStepService {
  startTour(): Observable<JoyrideStepInfo>;
  close(): unknown;
  back(): unknown;
  next(): unknown;
}

@Injectable({ providedIn: 'root' })
export class JoyrideStepService implements IJoyrideStepService {
  private static readonly SCROLLBAR_SIZE: number = 20;

  private currentStep: JoyrideStep;
  private winTopPosition: number = 0;
  private winBottomPosition: number = 0;
  private stepsObserver: ReplaySubject<JoyrideStepInfo> = new ReplaySubject<JoyrideStepInfo>();
  private readonly browserWindow = inject(WINDOW_TOKEN);
  private readonly backDropService = inject(JoyrideBackdropService);
  private readonly eventListener = inject(JoyrideEventListenerService);
  private readonly stepsContainerService = inject(JoyrideStepsContainerService);
  private readonly documentService = inject(JoyrideDocumentService);
  private readonly stepDrawerService = inject(JoyrideStepDrawerService);
  private readonly optionsService = inject(JoyrideOptionsService);

  constructor() {
    this.initViewportPositions();
    this.subscribeToScrollEvents();
    this.subscribeToResizeEvents();
  }

  startTour(): ReplaySubject<JoyrideStepInfo> {
    this.stepsObserver = new ReplaySubject<JoyrideStepInfo>();
    this.stepsContainerService.init();
    this.documentService.setDocumentHeight();

    this.tryShowStep(StepActionType.NEXT);
    this.eventListener.startListeningResizeEvents();
    this.eventListener.startListeningScrollEvents();
    this.subscribeToStepsUpdates();
    return this.stepsObserver;
  }

  close(): void {
    this.removeCurrentStep();
    this.notifyTourIsFinished();
    this.browserWindow.scrollTo(0, 0);
    this.eventListener.stopListeningResizeEvents();
    this.eventListener.stopListeningScrollEvents();
    this.backDropService.remove();
  }

  back(): void {
    this.removeCurrentStep();
    this.currentStep.backCliked.emit();
    this.tryShowStep(StepActionType.PREV);
  }

  next(): void {
    this.removeCurrentStep();
    this.currentStep.nextClicked.emit();
    this.tryShowStep(StepActionType.NEXT);
  }

  private subscribeToStepsUpdates(): void {
    this.stepsContainerService.stepHasBeenModified.subscribe((updatedStep: JoyrideStep) => {
      if (this.currentStep && this.currentStep.name === updatedStep.name) {
        this.currentStep = updatedStep;
      }
    });
  }

  private initViewportPositions(): void {
    this.winTopPosition = 0;
    this.winBottomPosition = this.browserWindow.innerHeight - JoyrideStepService.SCROLLBAR_SIZE;
  }

  private subscribeToScrollEvents(): void {
    this.eventListener.scrollEvent.subscribe((scroll: JoyrideScroll) => {
      this.winTopPosition = scroll.scrollY;
      this.winBottomPosition = this.winTopPosition + this.browserWindow.innerHeight - JoyrideStepService.SCROLLBAR_SIZE;
      if (this.currentStep) {
        this.backDropService.redraw(this.currentStep, scroll);
      }
    });
  }

  private subscribeToResizeEvents(): void {
    this.eventListener.resizeEvent.subscribe(() => {
      if (this.currentStep) {
        const stepInstance = this.currentStep.stepInstance;
        if (stepInstance === undefined || !this.currentStep.usedAsCoachMark) {
          this.backDropService.redrawTarget(this.currentStep);
        } else if (stepInstance && this.currentStep.usedAsCoachMark) {
          setTimeout( () => {
            this.backDropService.redrawTarget(this.currentStep);
            stepInstance.redrawStep();
          }, 100);
        }
      }
    });
  }

  private drawStep(step: JoyrideStep): void {
    step.position = step.position === JoyrideConstants.NO_POSITION ? this.optionsService.getStepDefaultPosition() : step.position;
    this.stepDrawerService.draw(step);
  }

  private async tryShowStep(actionType: StepActionType): Promise<void> {
    const timeout = this.optionsService.getWaitingTime();
    if (timeout > 100) {
      this.backDropService.remove();
    }
    setTimeout(() => {
      try {
        this.showStep(actionType);
      } catch (error) {
        if (error instanceof JoyrideStepDoesNotExist) {
          this.tryShowStep(actionType);
        } else if (error instanceof JoyrideStepOutOfRange) {
          // Forcing the tour closure: First or Last step not found in the DOM.
          if (actionType === StepActionType.PREV) {
            const currentStepNumber = this.stepsContainerService.getStepNumber(this.currentStep.name);
            this.stepsObserver.next({
              stepNumber: currentStepNumber - 1,
              name: undefined,
              actionType
            });
          }
          this.close();
        } else {
          throw new Error(String(error), { cause: error });
        }
      }
    }, timeout);
  }

  private showStep(actionType: StepActionType): void {
    this.currentStep = this.stepsContainerService.get(actionType);

    if (this.currentStep == null) {
      throw new JoyrideStepDoesNotExist('');
    }
    this.notifyStepClicked(actionType);
    // Scroll the element to get it visible if it's in a scrollable element
    this.scrollIfElementBeyondOtherElements();
    this.backDropService.draw(this.currentStep);
    this.drawStep(this.currentStep);

    if (!this.currentStep.usedAsCoachMark) {
      const timeout = this.optionsService.getWaitingTime();
      setTimeout(() => {
        this.scrollIfStepAndTargetAreNotVisible();
      }, timeout);
    }
  }

  private notifyStepClicked(actionType: StepActionType): void {
    const stepInfo: JoyrideStepInfo = {
      stepNumber: this.stepsContainerService.getStepNumber(this.currentStep.name),
      name: this.currentStep.name,
      actionType
    };
    this.stepsObserver.next(stepInfo);
  }

  private notifyTourIsFinished(): void {
    if (this.currentStep) {
      this.currentStep.tourDone.emit(undefined);
    }
    this.stepsObserver.complete();
  }

  private removeCurrentStep(): void {
    if (this.currentStep) {
      this.stepDrawerService.remove(this.currentStep);
    }
  }

  private scrollIfStepAndTargetAreNotVisible(): void {
    this.scrollWhenTargetOrStepAreHiddenBottom();
    this.scrollWhenTargetOrStepAreHiddenTop();
  }

  private scrollWhenTargetOrStepAreHiddenBottom(): void {
    const totalTargetBottom = this.getMaxTargetAndStepBottomPosition();
    if (totalTargetBottom > this.winBottomPosition) {
      this.browserWindow.scrollBy(0, totalTargetBottom - this.winBottomPosition);
    }
  }

  private scrollWhenTargetOrStepAreHiddenTop(): void {
    const totalTargetTop = this.getMaxTargetAndStepTopPosition();
    if (totalTargetTop < this.winTopPosition) {
      this.browserWindow.scrollBy(0, totalTargetTop - this.winTopPosition);
    }
  }

  private getMaxTargetAndStepBottomPosition(): number {
    const targetAbsoluteTop = this.documentService.getElementAbsoluteTop(this.currentStep.targetViewContainer.element);
    if (this.currentStep.position === 'top') {
      return targetAbsoluteTop + this.currentStep.stepInstance.targetHeight;
    } else if (this.currentStep.position === 'bottom') {
      return (
        targetAbsoluteTop +
        this.currentStep.stepInstance.targetHeight +
        this.currentStep.stepInstance.stepHeight +
        JoyrideConstants.ARROW_SIZE +
        JoyrideConstants.DISTANCE_FROM_TARGET
      );
    } else if (this.currentStep.position === 'right' || this.currentStep.position === 'left') {
      return Math.max(
        targetAbsoluteTop + this.currentStep.stepInstance.targetHeight,
        targetAbsoluteTop + this.currentStep.stepInstance.targetHeight / 2 + this.currentStep.stepInstance.stepHeight / 2
      );
    }
  }

  private getMaxTargetAndStepTopPosition(): number {
    const targetAbsoluteTop = this.documentService.getElementAbsoluteTop(this.currentStep.targetViewContainer.element);
    if (this.currentStep.position === 'top') {
      return targetAbsoluteTop - (
        this.currentStep.stepInstance.stepHeight +
        JoyrideConstants.ARROW_SIZE +
        JoyrideConstants.DISTANCE_FROM_TARGET
      );
    } else if (this.currentStep.position === 'bottom') {
      return targetAbsoluteTop;
    } else if (this.currentStep.position === 'right' || this.currentStep.position === 'left') {
      return Math.min(
        targetAbsoluteTop,
        targetAbsoluteTop + this.currentStep.stepInstance.targetHeight / 2 - this.currentStep.stepInstance.stepHeight / 2
      );
    }
  }

  private scrollIfElementBeyondOtherElements(): void {
    if (this.isElementBeyondOthers() === 2) {
      this.documentService.scrollToTheBottom(this.currentStep.targetViewContainer.element);
    }
    if (this.isElementBeyondOthers() === 2) {
      this.documentService.scrollToTheTop(this.currentStep.targetViewContainer.element);
    }
    if (this.isElementBeyondOthers() === 1 && this.documentService.isParentScrollable(this.currentStep.targetViewContainer.element)) {
      this.documentService.scrollIntoView(this.currentStep.targetViewContainer.element, this.currentStep.isElementOrAncestorFixed);
    }
    if (this.isElementBeyondOthers() === 1 && this.documentService.isParentScrollable(this.currentStep.targetViewContainer.element)) {
      this.currentStep.targetViewContainer.element.nativeElement.scrollIntoView();
    }
  }

  private isElementBeyondOthers(): number {
    return this.documentService.isElementBeyondOthers(
      this.currentStep.targetViewContainer.element,
      this.currentStep.isElementOrAncestorFixed,
      'backdrop'
    );
  }
}
